import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BaseResponse } from "app/business/common/baseresponse";
import { PaginationRequest } from "app/business/common/paginationrequest";
import { FinishProduct } from "app/models/master/finish-product";

import { environment } from "environments/environment";

@Injectable({
    providedIn: 'root',
})
export class FinishProductService {
    private readonly basePath = '/FinishProductPg';
    /** Deduplicate concurrent GetFinishProductById calls for the same id. */
    private readonly finishProductByIdInflight = new Map<string, Promise<BaseResponse>>();

    constructor(private http: HttpClient) { }

    async getFinishProducts(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/GetFinishProducts', paginationRequest)
            .toPromise() as BaseResponse;
    }

    async getFitterflyProducts(params: {
        page?: number;
        pageSize?: number;
        search?: string;
        recipeType?: string;
        recipeTypes?: string[];
        brandNames?: string[];
        dietaryPreferenceFilter?: string[];
        isCompleted?: boolean | string;
        id?: string;
        isUsdaBranded?: boolean;
        hasImage?: boolean;
        nutrientSortBy?: string;
        nutrientSortDirection?: string;
        nutrientBasis?: string;
    }): Promise<BaseResponse> {
        let httpParams = new HttpParams();

        if (params.page != null) httpParams = httpParams.set('page', String(params.page));
        if (params.pageSize != null) httpParams = httpParams.set('pageSize', String(params.pageSize));
        if (params.search) httpParams = httpParams.set('search', params.search);
        if (params.recipeType) httpParams = httpParams.set('recipeType', params.recipeType);
        (params.recipeTypes || []).forEach((v) => {
            if (v) httpParams = httpParams.append('recipeTypes', v);
        });
        (params.brandNames || []).forEach((v) => {
            if (v) httpParams = httpParams.append('brandNames', v);
        });
        (params.dietaryPreferenceFilter || []).forEach((v) => {
            if (v) httpParams = httpParams.append('dietaryPreferenceFilter', v);
        });
        if (params.isCompleted != null && params.isCompleted !== '') {
            httpParams = httpParams.set('isCompleted', String(params.isCompleted));
        }
        if (params.id) httpParams = httpParams.set('id', params.id);
        if (params.isUsdaBranded) httpParams = httpParams.set('isUsdaBranded', 'true');
        if (params.hasImage === true) httpParams = httpParams.set('hasImage', 'true');
        else if (params.hasImage === false) httpParams = httpParams.set('hasImage', 'false');
        if (params.nutrientSortBy) httpParams = httpParams.set('nutrientSortBy', params.nutrientSortBy);
        if (params.nutrientSortDirection) httpParams = httpParams.set('nutrientSortDirection', params.nutrientSortDirection);
        if (params.nutrientBasis) httpParams = httpParams.set('nutrientBasis', params.nutrientBasis);

        return await this.http.get(environment.baseUrl + 'api/v1/fitterfly-products', { params: httpParams })
            .toPromise() as BaseResponse;
    }

    async getFitterflyBrands(params: {
        search?: string;
        isUsdaBranded?: boolean;
    } = {}): Promise<BaseResponse> {
        let httpParams = new HttpParams();
        if (params.search) httpParams = httpParams.set('search', params.search);
        if (params.isUsdaBranded) httpParams = httpParams.set('isUsdaBranded', 'true');
        return await this.http.get(environment.baseUrl + 'api/v1/fitterfly-products/brands', { params: httpParams })
            .toPromise() as BaseResponse;
    }

    async getFitterflyProductById(id: string, isUsdaBranded?: boolean): Promise<BaseResponse> {
        let url = environment.baseUrl + `api/v1/fitterfly-products/${id}`;
        if (isUsdaBranded) url += '?isUsdaBranded=true';
        return await this.http.get(url)
            .toPromise() as BaseResponse;
    }

    async deleteFitterflyProduct(id: string): Promise<BaseResponse> {
        return await this.http.delete(environment.baseUrl + `api/v1/fitterfly-products/${id}`)
            .toPromise() as BaseResponse;
    }

    async updateMasterFinishProduct(id: string, payload: any, isUsdaBranded?: boolean): Promise<BaseResponse> {
        let url = environment.baseUrl + `api/v1/fitterfly-products/superadmin/master-products/${id}`;
        if (isUsdaBranded) {
            url += '?isUsdaBranded=true';
        }
        return await this.http.put(url, payload)
            .toPromise() as BaseResponse;
    }

    async getAssignedProducts(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/GetAssignedProducts', paginationRequest)
            .toPromise() as BaseResponse;
    }

    async getFirstReviewProducts(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/GetFirstReviewProducts', paginationRequest)
            .toPromise() as BaseResponse;
    }

    async getCorrectionsNeededProducts(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/GetCorrectionsNeededProducts', paginationRequest)
            .toPromise() as BaseResponse;
    }

    async getAssignerToBeAssignedProducts(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/GetAssignerToBeAssignedProducts', paginationRequest)
            .toPromise() as BaseResponse;
    }

    async assignProductToMaker(payload: any): Promise<BaseResponse> {
        payload.assignmentType = 'Maker';
        // Match the FinishProductAssignRequestDto property name
        if (payload.makerEmail && !payload.assignedTo) payload.assignedTo = payload.makerEmail;
        return await this.http.post(environment.apiUrl + this.basePath + '/Assign', payload)
            .toPromise() as BaseResponse;
    }

    async assignProductToChecker(payload: any): Promise<BaseResponse> {
        payload.assignmentType = 'Checker';
        if (payload.checkerEmail && !payload.assignedTo) payload.assignedTo = payload.checkerEmail;
        return await this.http.post(environment.apiUrl + this.basePath + '/Assign', payload)
            .toPromise() as BaseResponse;
    }

    async assignProductToMakerAndChecker(payload: any): Promise<BaseResponse> {
        const request = {
            productId: payload.productId || payload.recipeId,
            makerEmail: payload.makerEmail,
            checkerEmail: payload.checkerEmail,
            makerDueDate: payload.makerDueDate || null,
            checkerDueDate: payload.checkerDueDate || null,
            dueDate: payload.dueDate || payload.makerDueDate || payload.checkerDueDate || null,
            priority: payload.priority || 'Medium',
            notes: payload.notes || null
        };
        return await this.http.post(environment.apiUrl + this.basePath + '/AssignMakerAndChecker', request)
            .toPromise() as BaseResponse;
    }

    async reassignProduct(payload: any): Promise<BaseResponse> {
        const request = {
            productId: payload.productId || payload.recipeId,
            makerEmail: payload.makerEmail,
            checkerEmail: payload.checkerEmail,
            makerDueDate: payload.makerDueDate || null,
            checkerDueDate: payload.checkerDueDate || null,
            dueDate: payload.dueDate || payload.makerDueDate || payload.checkerDueDate || null,
            priority: payload.priority || 'Medium',
            reason: payload.reason || null,
            notes: payload.notes || payload.reason || null
        };
        return await this.http.post(environment.apiUrl + this.basePath + '/ReassignProduct', request)
            .toPromise() as BaseResponse;
    }

    async getAssignmentHistory(productId: number | string): Promise<BaseResponse> {
        return await this.http.get(environment.apiUrl + this.basePath + '/GetAssignmentHistory/' + productId)
            .toPromise() as BaseResponse;
    }

    async bulkAssignProducts(payload: any): Promise<BaseResponse> {
        payload.assignmentType = 'Maker';
        if (payload.makerEmail && !payload.assignedTo) payload.assignedTo = payload.makerEmail;
        if (payload.recipeIds && !payload.productIds) payload.productIds = payload.recipeIds;
        return await this.http.post(environment.apiUrl + this.basePath + '/BulkAssign', payload)
            .toPromise() as BaseResponse;
    }

    async bulkAssignProductsToMakerAndChecker(payload: any): Promise<BaseResponse> {
        const request = {
            productIds: payload.productIds || payload.recipeIds,
            makerEmail: payload.makerEmail,
            checkerEmail: payload.checkerEmail,
            makerDueDate: payload.makerDueDate || null,
            checkerDueDate: payload.checkerDueDate || null,
            dueDate: payload.dueDate || payload.makerDueDate || payload.checkerDueDate || null,
            priority: payload.priority || 'Medium',
            notes: payload.notes || null
        };
        return await this.http.post(environment.apiUrl + this.basePath + '/BulkAssignMakerAndChecker', request)
            .toPromise() as BaseResponse;
    }

    async getAssignerAssignedProducts(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/GetAssignerAssignedProducts', paginationRequest)
            .toPromise() as BaseResponse;
    }

    async getCorrectionsDoneProducts(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/GetCorrectionsDoneProducts', paginationRequest)
            .toPromise() as BaseResponse;
    }

    async getDashboardStatistics(type?: string): Promise<BaseResponse> {
        const params: any = {};
        if (type) params.type = type;
        return await this.http.get(environment.apiUrl + this.basePath + '/GetDashboardStatistics', { params })
            .toPromise() as BaseResponse;
    }

    async getCheckerDashboardStatistics(type?: string): Promise<BaseResponse> {
        const params: any = {};
        if (type) params.type = type;
        return await this.http.get(environment.apiUrl + this.basePath + '/GetCheckerDashboardStatistics', { params })
            .toPromise() as BaseResponse;
    }

    async getMakerDashboardStatistics(type?: string): Promise<BaseResponse> {
        const params: any = {};
        if (type) params.type = type;
        return await this.http.get(environment.apiUrl + this.basePath + '/GetMakerDashboardStatistics', { params })
            .toPromise() as BaseResponse;
    }

    /** Maps UI tab to API module filter: Recipes | Products | Supplements */
    static dashboardModuleFromTab(tab: 'recipe' | 'product' | 'supplement'): string {
        switch (tab) {
            case 'recipe': return 'Recipe';
            case 'supplement': return 'Supplement';
            default: return 'FinishProduct';
        }
    }

    async saveSectionApproval(approvalData: {
        productId: number | string;
        sectionNumber: number;
        sectionName?: string;
        approvalStatus: string;
        approvedBy?: string;
        approvedByName?: string;
        remarks?: string;
    }): Promise<BaseResponse> {
        const payload = {
            productId: Number(approvalData.productId) || 0,
            sectionNumber: approvalData.sectionNumber,
            sectionName: approvalData.sectionName,
            approvalStatus: approvalData.approvalStatus,
            approvedBy: approvalData.approvedBy,
            approvedByName: approvalData.approvedByName,
            remarks: approvalData.remarks
        };
        return await this.http.post(environment.apiUrl + this.basePath + '/SaveSectionApproval', payload)
            .toPromise() as BaseResponse;
    }

    async getSectionApprovals(productId: string): Promise<BaseResponse> {
        return await this.http.get(environment.apiUrl + `${this.basePath}/GetSectionApprovals/${productId}`)
            .toPromise() as BaseResponse;
    }

    async getSectionApprovalHistory(productId: string, sectionNumber?: number): Promise<BaseResponse> {
        let url = `${environment.apiUrl}${this.basePath}/GetSectionApprovalHistory/${productId}`;
        if (sectionNumber != null && sectionNumber > 0) {
            url += `?sectionNumber=${sectionNumber}`;
        }
        return await this.http.get(url).toPromise() as BaseResponse;
    }

    async getStatusHistory(productId: string): Promise<BaseResponse> {
        return await this.http.get(
            `${environment.apiUrl}${this.basePath}/GetStatusHistory/${productId}`
        ).toPromise() as BaseResponse;
    }

    async saveSystemLog(logData: any): Promise<BaseResponse> {
        try {
            return await this.http.post(environment.apiUrl + this.basePath + '/SaveSystemLog', logData)
                .toPromise() as BaseResponse;
        } catch (error: any) {
            const message =
                error?.error?.message ||
                error?.message ||
                'Unable to save system log.';
            return { status: 1, message, response: null };
        }
    }

    async getSystemLogs(productId: string): Promise<BaseResponse> {
        try {
            return await this.http.get(environment.apiUrl + `${this.basePath}/GetSystemLogs/${productId}`)
                .toPromise() as BaseResponse;
        } catch (error: any) {
            const message =
                error?.error?.message ||
                error?.message ||
                'Unable to load system logs.';
            return { status: 1, message, response: null };
        }
    }

    async getDietaryPreferences(): Promise<BaseResponse> {
        return await this.http.get(environment.apiUrl + this.basePath + '/GetDietaryPreferences')
            .toPromise() as BaseResponse;
    }

    async getHealthColorCodes(): Promise<BaseResponse> {
        return await this.http.get(environment.apiUrl + this.basePath + '/GetHealthColorCodes')
            .toPromise() as BaseResponse;
    }

    async getSuperAdminDashboardStatistics(checkerFilter?: string, type?: string): Promise<BaseResponse> {
        const params: any = {};
        if (checkerFilter) params.checkerFilter = checkerFilter;
        if (type) params.type = type;
        return await this.http.get(environment.apiUrl + this.basePath + '/GetSuperAdminDashboardStatistics', { params })
            .toPromise() as BaseResponse;
    }

    async getWeeklyStatusData(type?: string): Promise<BaseResponse> {
        const params: any = {};
        if (type) params.type = type;
        return await this.http.get(environment.apiUrl + this.basePath + '/GetWeeklyStatusData', { params })
            .toPromise() as BaseResponse;
    }

    async getProductActivityTrend(days: number = 7): Promise<BaseResponse> {
        return await this.http.get(environment.apiUrl + this.basePath + '/GetActivityTrend', { params: { days: String(days) } })
            .toPromise() as BaseResponse;
    }

    /** Activity trend for any entity type via the unified FinishProductPg endpoint. */
    async getActivityTrend(type: 'Recipe' | 'FinishProduct' | 'Supplement' | 'All' = 'FinishProduct', days: number = 7): Promise<BaseResponse> {
        return await this.http.get(
            environment.apiUrl + this.basePath + '/GetActivityTrend',
            { params: { type, days: String(days) } }
        ).toPromise() as BaseResponse;
    }

    async getTaskTrackingData(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/GetTaskTrackingData', paginationRequest, {
            headers: {
                'Content-Type': 'application/json'
            }
        }).toPromise() as BaseResponse;
    }

    async getCheckerTaskTrackingData(paginationRequest: PaginationRequest, type?: string): Promise<BaseResponse> {
        const params: any = {};
        if (type) params.type = type;
        return await this.http.post(environment.apiUrl + this.basePath + '/GetCheckerTaskTrackingData', paginationRequest, {
            params,
            headers: {
                'Content-Type': 'application/json'
            }
        }).toPromise() as BaseResponse;
    }

    async getMakerTaskTrackingData(paginationRequest: PaginationRequest, type?: string): Promise<BaseResponse> {
        const params: any = {};
        if (type) params.type = type;
        return await this.http.post(environment.apiUrl + this.basePath + '/GetMakerTaskTrackingData', paginationRequest, {
            params,
            headers: {
                'Content-Type': 'application/json'
            }
        }).toPromise() as BaseResponse;
    }

    async getTrackerData(request: { userType: string; entityType: string; pageNo: number; pageSize: number; search?: string }): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/GetTrackerData', request, {
            headers: {
                'Content-Type': 'application/json'
            }
        }).toPromise() as BaseResponse;
    }

    async getAssignedToMakerProducts(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/GetAssignedToMakerProducts', paginationRequest, {
            headers: {
                'Content-Type': 'application/json'
            }
        }).toPromise() as BaseResponse;
    }

    async getAssignedToCheckerProducts(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/GetAssignedToCheckerProducts', paginationRequest, {
            headers: {
                'Content-Type': 'application/json'
            }
        }).toPromise() as BaseResponse;
    }

    // Dedicated Maker APIs - Separate from generic APIs for better filtering
    async getMakerAssignedProducts(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/GetMakerAssignedProducts', paginationRequest, {
            headers: {
                'Content-Type': 'application/json'
            }
        }).toPromise() as BaseResponse;
    }

    async getMakerFirstReviewProducts(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/GetMakerFirstReviewProducts', paginationRequest, {
            headers: {
                'Content-Type': 'application/json'
            }
        }).toPromise() as BaseResponse;
    }

    async getMakerCorrectionsNeededProducts(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/GetMakerCorrectionsNeededProducts', paginationRequest, {
            headers: {
                'Content-Type': 'application/json'
            }
        }).toPromise() as BaseResponse;
    }

    async getMakerCorrectionsDoneProducts(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/GetMakerCorrectionsDoneProducts', paginationRequest, {
            headers: {
                'Content-Type': 'application/json'
            }
        }).toPromise() as BaseResponse;
    }

    async getMakerPublishedProducts(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/GetMakerPublishedProducts', paginationRequest, {
            headers: {
                'Content-Type': 'application/json'
            }
        }).toPromise() as BaseResponse;
    }

    // Dedicated Checker APIs - Separate from generic APIs for better filtering
    async getCheckerAssignedProducts(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/GetCheckerAssignedProducts', paginationRequest, {
            headers: {
                'Content-Type': 'application/json'
            }
        }).toPromise() as BaseResponse;
    }

    async getCheckerChangesNeededProducts(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/GetCheckerChangesNeededProducts', paginationRequest, {
            headers: {
                'Content-Type': 'application/json'
            }
        }).toPromise() as BaseResponse;
    }

    async getCheckerFirstReviewProducts(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/GetCheckerFirstReviewProducts', paginationRequest, {
            headers: {
                'Content-Type': 'application/json'
            }
        }).toPromise() as BaseResponse;
    }

    async getCheckerCorrectionsNeededProducts(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/GetCheckerCorrectionsNeededProducts', paginationRequest, {
            headers: {
                'Content-Type': 'application/json'
            }
        }).toPromise() as BaseResponse;
    }

    async getCheckerCorrectionsDoneProducts(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/GetCheckerCorrectionsDoneProducts', paginationRequest, {
            headers: {
                'Content-Type': 'application/json'
            }
        }).toPromise() as BaseResponse;
    }

    async getCheckerPublishedProducts(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/GetCheckerPublishedProducts', paginationRequest).toPromise() as BaseResponse;
    }

    async getCheckerCheckedProducts(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/GetCheckerCheckedProducts', paginationRequest).toPromise() as BaseResponse;
    }

    async getCheckerRejectedProducts(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/GetCheckerRejectedProducts', paginationRequest).toPromise() as BaseResponse;
    }

    async getApproverCheckedProducts(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/GetApproverCheckedProducts', paginationRequest, {
            headers: {
                'Content-Type': 'application/json'
            }
        }).toPromise() as BaseResponse;
    }

    async getApproverPublishedProducts(paginationRequest: PaginationRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/GetApproverPublishedProducts', paginationRequest, {
            headers: {
                'Content-Type': 'application/json'
            }
        }).toPromise() as BaseResponse;
    }

    async getFinishProductById(id: string): Promise<BaseResponse> {
        const key = String(id || '').trim();
        if (!key) {
            return { status: 1, message: 'Product id is required.', response: null };
        }

        const existing = this.finishProductByIdInflight.get(key);
        if (existing) {
            return existing;
        }

        const request = (async (): Promise<BaseResponse> => {
            try {
                return await this.http.get(environment.apiUrl + this.basePath + '/GetFinishProductById/' + key)
                    .toPromise() as BaseResponse;
            } catch (error: any) {
                const message =
                    error?.error?.message ||
                    error?.message ||
                    'Unable to reach the server while loading the product.';
                return {
                    status: 1,
                    message,
                    response: null
                };
            } finally {
                this.finishProductByIdInflight.delete(key);
            }
        })();

        this.finishProductByIdInflight.set(key, request);
        return request;
    }

    async addFinishProduct(wrappedData: any): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/AddFinishProduct', wrappedData)
            .toPromise() as BaseResponse;
    }

    /**
     * Bulk-create many finish products in one request (RDMS bulk upload).
     * POST api/FinishProductPg/BulkCreateFinishProducts
     */
    async bulkCreateFinishProducts(items: Array<{
        finishProductDto: Record<string, unknown>;
        assignment?: {
            mode?: string;
            makerEmail?: string | null;
            checkerEmail?: string | null;
            dueDate?: string | null;
            priority?: string;
            notes?: string | null;
        } | null;
    }>): Promise<BaseResponse> {
        return await this.http.post(
            environment.apiUrl + this.basePath + '/BulkCreateFinishProducts',
            { items },
        ).toPromise() as BaseResponse;
    }

    async updateFinishProduct(wrappedData: any): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/UpdateFinishProduct', wrappedData)
            .toPromise() as BaseResponse;
    }

    async deleteFinishProduct(id: string): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/DeleteFinishProduct/' + id, {})
            .toPromise() as BaseResponse;
    }

    async restoreFinishProduct(id: string): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/RestoreFinishProduct/' + id, {})
            .toPromise() as BaseResponse;
    }

    async uploadProductImages(files: File[]): Promise<BaseResponse> {
        const formData = new FormData();
        
        files.forEach((file, index) => {
            formData.append(`file${index}`, file);
        });
        
        return await this.http.post(environment.apiUrl + this.basePath + '/UploadProductImages', formData)
            .toPromise() as BaseResponse;
    }

    // ===== WORKFLOW TRACKING METHODS =====

    async setProductToCorrectionNeeded(request: WorkflowStatusRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/SetProductToCorrectionNeeded', request)
            .toPromise() as BaseResponse;
    }

    async setProductToCorrectionDone(request: WorkflowStatusRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/SetProductToCorrectionDone', request)
            .toPromise() as BaseResponse;
    }

    async setProductToPublished(request: WorkflowStatusRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/SetProductToPublished', request)
            .toPromise() as BaseResponse;
    }

    async setProductToRedirectForChanges(request: WorkflowStatusRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/SetProductToRedirectForChanges', request)
            .toPromise() as BaseResponse;
    }

    async setProductToDraftOrToBeAssigned(request: WorkflowStatusRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/SetProductToDraftOrToBeAssigned', request)
            .toPromise() as BaseResponse;
    }

    async setProductToFirstReviewOrDraft(request: WorkflowStatusRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/SetProductToFirstReviewOrDraft', request)
            .toPromise() as BaseResponse;
    }

    async updateProductStatusWithWorkflowTracking(request: WorkflowStatusRequest): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/UpdateProductStatusWithWorkflowTracking', request)
            .toPromise() as BaseResponse;
    }

    async resetAllAssignmentData(): Promise<BaseResponse> {
        return await this.http.post(environment.apiUrl + this.basePath + '/ResetAllAssignmentData', {})
            .toPromise() as BaseResponse;
    }

    async getBillingByMaker(email: string): Promise<BaseResponse> {
        return await this.http.get(environment.apiUrl + this.basePath + `/GetBillingByMaker/${encodeURIComponent(email)}`)
            .toPromise() as BaseResponse;
    }

    async getBillingByChecker(email: string): Promise<BaseResponse> {
        return await this.http.get(environment.apiUrl + this.basePath + `/GetBillingByChecker/${encodeURIComponent(email)}`)
            .toPromise() as BaseResponse;
    }
}

// Workflow status request interface
export interface WorkflowStatusRequest {
    productId: string;
    status?: string;
    userEmail?: string;
    userName?: string;
    remark?: string;
}
